import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Flex,
  Group,
  List,
  Loader,
  Select,
  SimpleGrid,
  Slider,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconDownload,
  IconEye,
  IconFileText,
  IconSparkles,
  IconStairsUp,
  IconTrash,
} from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError, unwrap } from "../../api/client";
import {
  createSheet,
  deleteSheet,
  getCurriculum,
  listLibrary,
  listSheets,
  nextSheet,
  type LibrarySet,
  type Sheet,
} from "../../api/gen";
import { usePdf } from "../../components/PdfViewer";
import { SCHOOLS, schoolLabel, type School } from "../../lib/school";
import { shortDate } from "../../lib/text";
import { useOrg, useSession } from "../../lib/session";

// Arbeitsblätter.
//
// Paper the child works on away from the screen, and then, where it gets
// stuck, picks in Sokrates as "the sheet in front of me" — no photograph.
// Two kinds: the library's, written by hand, and the family's own, which the
// model writes to a topic of the Lehrplan the parent picks here.
//
// Difficulty is a five-step scale against the Lehrplan, and a sheet climbs
// inside itself: the first task is a quick win, the last one sits on the step
// the parent chose. "Nächstes Blatt" climbs across sheets — the server moves
// the step by how the child got on with this one in Sokrates.
//
// The answers are a separate download and say so. A sheet a child can see
// never carries them, because Sokrates does not give answers either.
export function SheetsPage() {
  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Title order={3}>Arbeitsblätter</Title>
        <Text c="dimmed" size="sm">
          Zum Ausdrucken. Ihr Kind bearbeitet das Blatt auf Papier. Wo es hängt,
          wählt es das Blatt in Sokrates aus und bekommt Fragen statt Lösungen —
          ohne Foto.
        </Text>
      </Stack>
      <WriteSheet />
      <WrittenSheets />
      <Library />
    </Stack>
  );
}

/** The five steps, from well below the Lehrplan to a little above it. */
const LEVELS = [
  { value: -2, label: "Deutlich leichter" },
  { value: -1, label: "Etwas leichter" },
  { value: 0, label: "Lehrplan" },
  { value: 1, label: "Etwas schwerer" },
  { value: 2, label: "Knobeln" },
];

const levelLabel = (level: number) =>
  LEVELS.find((l) => l.value === level)?.label ?? "Lehrplan";

function WriteSheet() {
  const org = useOrg();
  const { pairing } = useSession();
  const queryClient = useQueryClient();
  const { download, view, viewer } = usePdf();
  // The Lehrplan page links here with a topic, a child and a step, so a
  // parent who just marked a topic for reworking lands on a filled form.
  const [params] = useSearchParams();
  // A topic is school/subject/grade/area/topic. Without one, the form opens
  // on the school type and grade of the child it is for.
  const pre = (params.get("topic") ?? "").split("/");
  const children = pairing?.children ?? [];
  const childOf = (id: string | null) => children.find((c) => c.id === id);
  const preChild = childOf(params.get("child"));
  const [school, setSchool] = useState<School>(
    (pre[0] as School) || preChild?.school || "grundschule",
  );
  const [subject, setSubject] = useState<string | null>(pre[1] || null);
  const [grade, setGrade] = useState<string | null>(
    pre[2] || (preChild?.grade ? String(preChild.grade) : null),
  );
  const [area, setArea] = useState<string | null>(pre[3] || null);
  const [topic, setTopic] = useState<string | null>(pre[4] || null);
  const [childId, setChildId] = useState<string | null>(
    params.get("child") ?? "",
  );
  const pickSchool = (next: School) => {
    setSchool(next);
    setSubject(null);
    setGrade(null);
    setArea(null);
    setTopic(null);
  };
  // Picking a child before a subject moves the form to their school and grade.
  const pickChild = (id: string | null) => {
    setChildId(id);
    const c = childOf(id);
    if (c?.school && !subject) {
      pickSchool(c.school);
      if (c.grade) setGrade(String(c.grade));
    }
  };
  const [note, setNote] = useState("");
  const [level, setLevel] = useState(Number(params.get("level") ?? 0) || 0);
  // Five labels under a slider do not fit a phone; there the slider shows
  // its steps and the chosen one is named above it.
  const wide = useMediaQuery("(min-width: 48em)");
  const [written, setWritten] = useState<Sheet | null>(null);

  const { data: curriculum, isLoading } = useQuery({
    queryKey: ["curriculum", school],
    queryFn: () => unwrap(getCurriculum({ headers: org, query: { school } })),
    staleTime: Infinity,
    retry: false,
  });

  const subjects = useMemo(
    () => (curriculum?.subjects ?? []).filter((s) => s.worksheets),
    [curriculum],
  );
  const s = subjects.find((x) => x.id === subject);
  const g = s?.grades.find((x) => String(x.grade) === grade);
  const a = g?.areas.find((x) => x.id === area);
  const t = a?.topics.find((x) => x.id === topic);

  const write = useMutation({
    mutationFn: () =>
      unwrap(
        createSheet({
          headers: org,
          body: {
            school,
            subject: subject!,
            grade: Number(grade),
            area: area!,
            topic: topic!,
            level,
            ...(childId ? { childId } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
          },
        }),
      ),
    onSuccess: (sheet) => {
      setWritten(sheet ?? null);
      void queryClient.invalidateQueries({ queryKey: ["sheets"] });
    },
    onError: (err) => {
      const message =
        err instanceof ApiError && err.status === 402
          ? "Neue Arbeitsblätter brauchen ein Abo. Alles Bisherige bleibt da."
          : err instanceof ApiError && err.status === 503
            ? "Das Schreiben von Arbeitsblättern ist gerade nicht erreichbar."
            : (err as Error).message;
      notifications.show({ color: "red", message });
    },
  });

  const schoolPicker = (
    <Select
      label="Schulform"
      data={SCHOOLS.map((x) => ({ value: x.value, label: x.label }))}
      value={school}
      allowDeselect={false}
      onChange={(v) => v && pickSchool(v as School)}
    />
  );

  if (isLoading) return <Loader />;
  if (!curriculum) {
    return (
      <Card withBorder padding="lg">
        <Stack>
          <Title order={4}>Neues Blatt schreiben lassen</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>{schoolPicker}</SimpleGrid>
          <Alert variant="light">
            Für die {schoolLabel(school)} gibt es den Lehrplan hier noch nicht,
            also auch noch keine Blätter.
          </Alert>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group gap="xs">
          <IconSparkles size={20} />
          <Title order={4}>Neues Blatt schreiben lassen</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Wählen Sie ein Thema aus dem {curriculum.name}. Sokrates schreibt ein
          neues Blatt dazu, jedes Mal ein anderes, mit einer Lösungsseite nur
          für Sie.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {schoolPicker}
          <Select
            label="Fach"
            placeholder="Fach wählen"
            data={subjects.map((x) => ({ value: x.id, label: x.name }))}
            value={subject}
            onChange={(v) => {
              setSubject(v);
              // Keep the grade when the new subject is taught in it.
              const next = subjects.find((x) => x.id === v);
              if (!next?.grades.some((x) => String(x.grade) === grade))
                setGrade(null);
              setArea(null);
              setTopic(null);
            }}
          />
          <Select
            label="Klasse"
            placeholder="Klasse wählen"
            disabled={!s}
            data={(s?.grades ?? []).map((x) => ({
              value: String(x.grade),
              label: `Klasse ${x.grade}`,
            }))}
            value={grade}
            onChange={(v) => {
              setGrade(v);
              setArea(null);
              setTopic(null);
            }}
          />
          <Select
            label="Lernbereich"
            placeholder="Lernbereich wählen"
            disabled={!g}
            data={(g?.areas ?? [])
              .filter((x) => x.topics.length > 0)
              .map((x) => ({ value: x.id, label: x.name }))}
            value={area}
            onChange={(v) => {
              setArea(v);
              setTopic(null);
            }}
          />
          <Select
            label="Thema"
            placeholder="Thema wählen"
            disabled={!a}
            searchable
            data={(a?.topics ?? []).map((x) => ({
              value: x.id,
              label: x.name,
            }))}
            value={topic}
            onChange={setTopic}
          />
        </SimpleGrid>

        {t?.parentsOnly ? (
          <Alert variant="light" color="grape" title="Nur für Eltern">
            Ein Thema zum gemeinsamen Besprechen: Das Blatt sehen nur Sie. Ihr
            Kind findet es nicht in seiner Liste und kann es in Sokrates nicht
            öffnen.
          </Alert>
        ) : null}

        {t && t.goals.length > 0 ? (
          <Alert variant="light" title="Ihr Kind übt dabei">
            <List size="sm">
              {t.goals.map((goal) => (
                <List.Item key={goal}>{goal}</List.Item>
              ))}
            </List>
          </Alert>
        ) : null}

        <Stack gap={4} pb="lg">
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Schwierigkeit
            </Text>
            {!wide ? <Badge variant="light">{levelLabel(level)}</Badge> : null}
          </Group>
          <Text size="xs" c="dimmed">
            Jedes Blatt beginnt mit einem schnellen Erfolgserlebnis und steigt
            dann bis zu dieser Stufe an. „Knobeln“ bleibt beim Stoff der Klasse,
            verlangt aber mehr Nachdenken.
          </Text>
          <Slider
            min={-2}
            max={2}
            step={1}
            value={level}
            onChange={setLevel}
            label={levelLabel}
            marks={wide ? LEVELS : LEVELS.map(({ value }) => ({ value }))}
            mx={wide ? 48 : "xs"}
            mt="xs"
          />
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Select
            label="Für"
            data={[
              { value: "", label: "die ganze Familie" },
              ...children.map((c) => ({ value: c.id, label: c.name })),
            ]}
            value={childId}
            onChange={pickChild}
            allowDeselect={false}
          />
          <Textarea
            label="Wunsch (optional)"
            placeholder="z. B. mehr Textaufgaben, oder: tut sich mit dem Übertrag schwer"
            autosize
            minRows={1}
            maxRows={4}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        </SimpleGrid>

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Das dauert ein paar Sekunden. Der Name Ihres Kindes wird dafür nicht
            weitergegeben.
          </Text>
          <Button
            leftSection={<IconSparkles size={18} />}
            disabled={!t}
            loading={write.isPending}
            onClick={() => write.mutate()}
          >
            Blatt schreiben
          </Button>
        </Group>

        {written ? (
          <Card
            withBorder
            radius="md"
            padding="md"
            bg="var(--mantine-color-green-light)"
          >
            <Stack gap="xs">
              <Text fw={600}>Fertig: {written.title}</Text>
              <List size="sm" type="ordered">
                {written.tasks.map((task) => (
                  <List.Item key={task}>{task}</List.Item>
                ))}
              </List>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconEye size={16} />}
                  onClick={() =>
                    void view(`/sheets/${written.id}/pdf`, written.title)
                  }
                >
                  Ansehen
                </Button>
                <Button
                  size="xs"
                  leftSection={<IconDownload size={16} />}
                  onClick={() => void download(`/sheets/${written.id}/pdf`)}
                >
                  Blatt herunterladen
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconEye size={16} />}
                  onClick={() =>
                    void view(
                      `/sheets/${written.id}/pdf?answers=true`,
                      `${written.title} – mit Lösungen`,
                    )
                  }
                >
                  Lösungen ansehen
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() =>
                    void download(`/sheets/${written.id}/pdf?answers=true`)
                  }
                >
                  Mit Lösungen
                </Button>
              </Group>
            </Stack>
          </Card>
        ) : null}
      </Stack>
      {viewer}
    </Card>
  );
}

function WrittenSheets() {
  const org = useOrg();
  const { pairing } = useSession();
  const queryClient = useQueryClient();
  const { download, view, viewer } = usePdf();

  const { data, isLoading } = useQuery({
    queryKey: ["sheets"],
    queryFn: () =>
      unwrap(
        listSheets({
          headers: org,
          query: { limit: 100, sort: "createdAt", sortDir: "desc" },
        }),
      ),
  });

  const next = useMutation({
    mutationFn: (id: string) =>
      unwrap(nextSheet({ headers: org, path: { id } })),
    onSuccess: (n) => {
      void queryClient.invalidateQueries({ queryKey: ["sheets"] });
      if (n) {
        notifications.show({
          title: `Nächstes Blatt: ${n.sheet.title} (${levelLabel(n.sheet.level)})`,
          message: n.reason,
          autoClose: 10_000,
        });
      }
    },
    onError: (err) =>
      notifications.show({ color: "red", message: (err as Error).message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(deleteSheet({ headers: org, path: { id } })),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["sheets"] }),
    onError: (err) =>
      notifications.show({ color: "red", message: (err as Error).message }),
  });

  const childName = (id?: string) =>
    pairing?.children.find((c) => c.id === id)?.name;

  return (
    <Stack>
      {viewer}
      <Title order={4}>Für euch geschrieben</Title>
      {isLoading ? (
        <Loader size="sm" />
      ) : !data || data.data.length === 0 ? (
        <Text size="sm" c="dimmed">
          Noch keine. Oben ein Thema wählen, und das erste Blatt steht hier.
        </Text>
      ) : (
        <Stack gap="xs">
          {data.data.map((sheet) => (
            <Card key={sheet.id} withBorder padding="sm">
              <Flex
                direction={{ base: "column", sm: "row" }}
                justify="space-between"
                align={{ base: "stretch", sm: "center" }}
                gap="xs"
              >
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Group gap="xs">
                    <Text fw={600} truncate>
                      {sheet.title}
                    </Text>
                    <Badge size="sm" variant="outline" color="gray">
                      {levelLabel(sheet.level)}
                    </Badge>
                    {sheet.childId ? (
                      <Badge size="sm" variant="light">
                        {childName(sheet.childId) ?? "Kind"}
                      </Badge>
                    ) : null}
                    {sheet.parentsOnly ? (
                      <Badge size="sm" variant="light" color="grape">
                        nur für Eltern
                      </Badge>
                    ) : null}
                  </Group>
                  <Text size="xs" c="dimmed" truncate>
                    {sheet.school !== "grundschule"
                      ? `${schoolLabel(sheet.school)} · `
                      : ""}
                    {sheet.subject} · Klasse {sheet.grade} · {sheet.topic} ·{" "}
                    {shortDate(sheet.createdAt)}
                  </Text>
                </Stack>
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Tooltip label="Nächstes Blatt zum selben Thema, je nachdem, wie es mit diesem lief">
                    <Button
                      size="compact-sm"
                      variant="light"
                      leftSection={<IconStairsUp size={16} />}
                      loading={next.isPending && next.variables === sheet.id}
                      onClick={() => next.mutate(sheet.id)}
                    >
                      Nächstes Blatt
                    </Button>
                  </Tooltip>
                  <Tooltip label="Blatt ansehen">
                    <ActionIcon
                      variant="subtle"
                      aria-label="Blatt ansehen"
                      onClick={() =>
                        void view(`/sheets/${sheet.id}/pdf`, sheet.title)
                      }
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Blatt herunterladen">
                    <ActionIcon
                      variant="light"
                      aria-label="Blatt herunterladen"
                      onClick={() => void download(`/sheets/${sheet.id}/pdf`)}
                    >
                      <IconDownload size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Lösungen ansehen">
                    <ActionIcon
                      variant="subtle"
                      aria-label="Lösungen ansehen"
                      onClick={() =>
                        void view(
                          `/sheets/${sheet.id}/pdf?answers=true`,
                          `${sheet.title} – mit Lösungen`,
                        )
                      }
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Mit Lösungen herunterladen">
                    <ActionIcon
                      variant="subtle"
                      aria-label="Mit Lösungen herunterladen"
                      onClick={() =>
                        void download(`/sheets/${sheet.id}/pdf?answers=true`)
                      }
                    >
                      <IconFileText size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Löschen">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label="Löschen"
                      loading={
                        remove.isPending && remove.variables === sheet.id
                      }
                      onClick={() => remove.mutate(sheet.id)}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Flex>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function Library() {
  const org = useOrg();
  const { data, isLoading } = useQuery({
    queryKey: ["library"],
    queryFn: () => unwrap(listLibrary({ headers: org })),
    staleTime: Infinity,
  });

  // Grade 5 has a set per school type, so the list narrows to one.
  const [school, setSchool] = useState<string>("all");
  const sets = (data?.sets ?? []).filter(
    (set) => school === "all" || set.school === school,
  );

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={4}>Aus der Bibliothek</Title>
        <Select
          aria-label="Schulform"
          w={180}
          size="xs"
          allowDeselect={false}
          data={[
            { value: "all", label: "Alle Schulformen" },
            ...SCHOOLS.map((x) => ({ value: x.value, label: x.label })),
          ]}
          value={school}
          onChange={(v) => setSchool(v ?? "all")}
        />
      </Group>
      {isLoading ? (
        <Loader size="sm" />
      ) : (
        sets.map((set) => <LibrarySetCard key={set.id} set={set} />)
      )}
    </Stack>
  );
}

function LibrarySetCard({ set }: { set: LibrarySet }) {
  const { download, view, viewer } = usePdf();
  const name = `${set.subject} · Klasse ${set.grade} · ${set.topic}`;
  return (
    <Card withBorder padding="lg">
      {viewer}
      <Stack gap="sm">
        <Group justify="space-between">
          <div>
            <Text fw={600}>
              {set.subject} ·{" "}
              {set.school !== "grundschule"
                ? `${schoolLabel(set.school)} `
                : ""}
              Klasse {set.grade} · {set.topic}
            </Text>
            <Text size="xs" c="dimmed">
              {set.curriculum}
            </Text>
          </div>
          <Group gap="xs">
            {set.bundle ? (
              <>
                <Tooltip label="Alle Blätter ansehen">
                  <ActionIcon
                    variant="subtle"
                    aria-label="Alle Blätter ansehen"
                    onClick={() =>
                      void view(`/library/${set.id}/bundle`, name)
                    }
                  >
                    <IconEye size={18} />
                  </ActionIcon>
                </Tooltip>
                <Button
                  size="xs"
                  leftSection={<IconDownload size={16} />}
                  onClick={() => void download(`/library/${set.id}/bundle`)}
                >
                  Alle {set.sheets.length} Blätter
                </Button>
              </>
            ) : null}
            {set.answers ? (
              <>
                <Tooltip label="Lösungen ansehen">
                  <ActionIcon
                    variant="subtle"
                    aria-label="Lösungen ansehen"
                    onClick={() =>
                      void view(
                        `/library/${set.id}/answers`,
                        `${name} – Lösungen`,
                      )
                    }
                  >
                    <IconEye size={18} />
                  </ActionIcon>
                </Tooltip>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => void download(`/library/${set.id}/answers`)}
                >
                  Lösungen
                </Button>
              </>
            ) : null}
          </Group>
        </Group>
        {set.sheets.map((sheet, i) => (
          <Group key={sheet.id} justify="space-between" wrap="nowrap">
            <Text size="sm">
              {i + 1}. {sheet.title}
            </Text>
            <Group gap={4} wrap="nowrap">
              <ActionIcon
                variant="subtle"
                aria-label={`${sheet.title} ansehen`}
                onClick={() =>
                  void view(`/library/${set.id}/${sheet.file}`, sheet.title)
                }
              >
                <IconEye size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                aria-label={`${sheet.title} herunterladen`}
                onClick={() =>
                  void download(`/library/${set.id}/${sheet.file}`)
                }
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Group>
          </Group>
        ))}
      </Stack>
    </Card>
  );
}
